import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { CheckCircle2, Loader2, Trophy, Sparkles, Crown, PlayCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import confetti from "canvas-confetti";
import { VideoPlayer } from "./VideoPlayer";

interface JourneyAcknowledgmentStepProps {
  stepId: string;
  userId: string;
  content: string;
  checkboxText: string;
  videoUrl?: string;
  showAffiliateLink?: boolean;
  triggersJourneyComplete?: boolean;
  isCompleted: boolean;
  onComplete: () => void;
  initialProgress?: number;
  videoIntroText?: string;
}

// Celebration confetti function - fires from both sides
const fireCelebrationConfetti = () => {
  const duration = 4000;
  const animationEnd = Date.now() + duration;
  const colors = ['#FFD700', '#FFA500', '#FF6347', '#FFE066', '#FBBF24', '#F59E0B'];

  const frame = () => {
    const timeLeft = animationEnd - Date.now();
    if (timeLeft <= 0) return;

    const particleCount = 3;
    
    // Left cannon
    confetti({
      particleCount,
      angle: 60,
      spread: 55,
      origin: { x: 0, y: 0.7 },
      colors,
      ticks: 300,
      gravity: 0.8,
      scalar: 1.2,
      drift: 0.5,
    });
    
    // Right cannon
    confetti({
      particleCount,
      angle: 120,
      spread: 55,
      origin: { x: 1, y: 0.7 },
      colors,
      ticks: 300,
      gravity: 0.8,
      scalar: 1.2,
      drift: -0.5,
    });

    if (timeLeft > 0) {
      requestAnimationFrame(frame);
    }
  };

  // Initial burst from center
  confetti({
    particleCount: 100,
    spread: 100,
    origin: { y: 0.6 },
    colors,
    ticks: 200,
  });

  frame();
};

export function JourneyAcknowledgmentStep({
  stepId,
  userId,
  content,
  checkboxText,
  videoUrl,
  showAffiliateLink,
  triggersJourneyComplete,
  isCompleted,
  onComplete,
  initialProgress = 0,
  videoIntroText,
}: JourneyAcknowledgmentStepProps) {
  const [isChecked, setIsChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [videoWatchedPercent, setVideoWatchedPercent] = useState(initialProgress);
  const [videoCompleted, setVideoCompleted] = useState(!videoUrl || initialProgress >= 90);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  // Refs to track latest values for cleanup
  const videoProgressRef = useRef(initialProgress);
  const lastSavedPercentRef = useRef(Math.floor(initialProgress / 5));
  const isCompletedRef = useRef(false);

  // Update ref when progress changes
  useEffect(() => {
    videoProgressRef.current = videoWatchedPercent;
  }, [videoWatchedPercent]);

  // Save progress on component unmount (when step is collapsed)
  useEffect(() => {
    return () => {
      // Don't save progress if step was just completed - avoid race condition
      if (isCompletedRef.current) return;
      
      const currentProgress = videoProgressRef.current;
      if (currentProgress > 0 && currentProgress > initialProgress) {
        // Fire and forget - save progress when unmounting
        supabase
          .from('user_journey_step_progress')
          .upsert({
            user_id: userId,
            step_id: stepId,
            status: 'active',
            action_data: { watch_percent: currentProgress },
          }, { onConflict: 'user_id,step_id' })
          .then(() => {
            // Invalidate cache so next mount gets fresh data
            queryClient.invalidateQueries({ queryKey: ['journey-steps-progress'] });
          });
      }
    };
  }, [userId, stepId, initialProgress, queryClient]);

  const handleVideoProgress = async (percent: number) => {
    const prevPercent = videoWatchedPercent;
    setVideoWatchedPercent(percent);
    
    // Save progress to database every 5% (more frequent than before)
    const currentInterval = Math.floor(percent / 5);
    if (currentInterval > lastSavedPercentRef.current) {
      lastSavedPercentRef.current = currentInterval;
      await supabase
        .from('user_journey_step_progress')
        .upsert({
          user_id: userId,
          step_id: stepId,
          status: 'active',
          action_data: { watch_percent: percent },
        }, { onConflict: 'user_id,step_id' });
      
      // Invalidate cache so parent has fresh data
      queryClient.invalidateQueries({ queryKey: ['journey-steps-progress'] });
    }
  };

  const handleVideoComplete = () => {
    setVideoCompleted(true);
  };

  const handleContinue = async () => {
    if (!isChecked) return;

    setIsSubmitting(true);
    // Prevent cleanup from overwriting completion
    isCompletedRef.current = true;
    
    try {
      // Mark step as completed
      const { error } = await supabase
        .from('user_journey_step_progress')
        .upsert({
          user_id: userId,
          step_id: stepId,
          status: 'completed',
          completed_at: new Date().toISOString(),
          verified_completion: true,
          action_data: { acknowledged: true },
        }, { onConflict: 'user_id,step_id' });

      if (error) throw error;

      // Get current step to find next one
      const { data: currentStep } = await supabase
        .from('journey_steps')
        .select('step_number')
        .eq('id', stepId)
        .single();

      if (currentStep) {
        const { data: nextStep } = await supabase
          .from('journey_steps')
          .select('id')
          .eq('step_number', currentStep.step_number + 1)
          .eq('is_active', true)
          .single();

        if (nextStep) {
          await supabase
            .from('user_journey_step_progress')
            .upsert({
              user_id: userId,
              step_id: nextStep.id,
              status: 'active',
              started_at: new Date().toISOString(),
            }, { onConflict: 'user_id,step_id' });
        }
      }

      // Handle journey completion trigger with celebration
      if (triggersJourneyComplete) {
        await supabase
          .from('user_journey_state')
          .upsert({
            user_id: userId,
            journey_completed: true,
            journey_completed_at: new Date().toISOString(),
          }, { onConflict: 'user_id' });

        // Fire confetti and show celebration
        setShowCelebration(true);
        fireCelebrationConfetti();

        // Delay the onComplete to let user enjoy the celebration
        setTimeout(() => {
          onComplete();
        }, 3500);
      } else {
        onComplete();
      }
    } catch (error) {
      console.error('Error completing step:', error);
      toast({
        title: "Error",
        description: "Failed to save progress. Please try again.",
        variant: "destructive",
      });
      setIsSubmitting(false);
    }
  };

  // Celebration state after clicking the final button
  if (showCelebration) {
    return (
      <div className="flex flex-col items-center justify-center py-12 animate-scale-in">
        <div className="relative">
          <div className="absolute inset-0 bg-gradient-to-r from-amber-400/20 via-yellow-400/20 to-amber-400/20 blur-xl rounded-full animate-pulse" />
          <Crown className="w-16 h-16 text-amber-500 relative z-10 animate-bounce" />
        </div>
        <h3 className="text-2xl font-bold mt-6 bg-gradient-to-r from-amber-600 via-yellow-500 to-amber-600 bg-clip-text text-transparent">
          Welcome, Steward!
        </h3>
        <p className="text-muted-foreground mt-2 text-center">
          Your journey has prepared you. Now let's build something great together.
        </p>
      </div>
    );
  }

  if (isCompleted) {
    return (
      <div className="space-y-4">
        {/* Show video for rewatching when completed */}
        {videoUrl && (
          <div className="relative">
            <VideoPlayer
              videoUrl={videoUrl}
              requiredWatchPercent={0}
              onProgress={() => {}}
              onComplete={() => {}}
              initialProgress={0}
            />
            <div className="absolute top-3 right-3 bg-green-600/90 text-white px-3 py-1 rounded-full flex items-center gap-1.5 text-sm font-medium shadow-lg pointer-events-none">
              <CheckCircle2 className="w-4 h-4" />
              <span>Completed</span>
            </div>
          </div>
        )}

        <div className="flex items-center gap-2 text-green-600">
          <CheckCircle2 className="w-5 h-5" />
          <span className="font-medium">Acknowledged</span>
        </div>
        
        {/* Show what was acknowledged */}
        <div className="bg-muted/30 rounded-lg p-4">
          <p className="text-sm text-muted-foreground whitespace-pre-line">{content}</p>
        </div>
        
        {checkboxText && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <CheckCircle2 className="w-4 h-4 text-green-500" />
            <span>{checkboxText}</span>
          </div>
        )}
      </div>
    );
  }

  // Video section JSX - not a component to avoid remounting on state changes
  const videoSection = videoUrl ? (
    <div className="space-y-3 mb-6">
      <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <PlayCircle className="w-5 h-5 text-blue-600 mt-0.5" />
        <div>
          <p className="font-medium text-blue-800">Watch the Video</p>
          <p className="text-sm text-blue-700">
            {videoIntroText || 'Please watch the video below before acknowledging.'}
          </p>
        </div>
      </div>
      
      <VideoPlayer
        videoUrl={videoUrl}
        requiredWatchPercent={90}
        onProgress={handleVideoProgress}
        onComplete={handleVideoComplete}
        initialProgress={initialProgress}
      />
      
      {!videoCompleted && (
        <p className="text-sm text-muted-foreground text-center">
          Video progress: {Math.round(videoWatchedPercent)}%. Watch to 90% to continue
        </p>
      )}
    </div>
  ) : null;

  // Final step gets special celebratory styling
  if (triggersJourneyComplete) {
    return (
      <div className="space-y-6">
        {/* Video Section */}
        {videoSection}

        {/* Show content only when video is complete */}
        {videoCompleted ? (
          <>
            {/* Celebratory Header */}
            <div className="text-center space-y-4 py-4">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-amber-400/20 via-yellow-400/20 to-orange-400/20 border border-amber-500/30">
                <Trophy className="w-8 h-8 text-amber-500" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">You've Made It!</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  This is the final step of your onboarding journey
                </p>
              </div>
            </div>

            {/* Content */}
            <div className="prose prose-sm max-w-none">
              <p className="text-muted-foreground whitespace-pre-line text-center">{content}</p>
            </div>

            {/* Affiliate Link */}
            {showAffiliateLink && (
              <div className="p-4 bg-gradient-to-r from-amber-50/50 to-yellow-50/50 dark:from-amber-950/20 dark:to-yellow-950/20 rounded-lg border border-amber-200/50 dark:border-amber-800/30">
                <p className="text-sm font-medium mb-2 text-amber-800 dark:text-amber-200">Share with loved ones:</p>
                <p className="text-xs text-amber-700/80 dark:text-amber-300/80">
                  Your unique referral link will be available in your settings after completing the journey.
                </p>
              </div>
            )}

            {/* Enhanced Checkbox for final step */}
            <div className="flex items-start gap-3 p-5 bg-gradient-to-r from-primary/5 via-primary/10 to-primary/5 border-2 border-primary/20 rounded-xl transition-all duration-300 hover:border-primary/40 hover:shadow-lg hover:shadow-primary/5">
              <Checkbox
                id={`ack-${stepId}`}
                checked={isChecked}
                onCheckedChange={(checked) => setIsChecked(checked === true)}
                className="mt-0.5 h-5 w-5 border-2 data-[state=checked]:bg-primary data-[state=checked]:border-primary"
              />
              <label
                htmlFor={`ack-${stepId}`}
                className="text-sm font-medium cursor-pointer leading-relaxed"
              >
                {checkboxText}
              </label>
            </div>

            {/* Enhanced Complete Button */}
            <div className="flex justify-center pt-2">
              <Button
                onClick={handleContinue}
                disabled={!isChecked || isSubmitting}
                size="lg"
                className="bg-gradient-to-r from-amber-500 via-yellow-500 to-amber-500 hover:from-amber-600 hover:via-yellow-600 hover:to-amber-600 text-white font-semibold px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:hover:scale-100"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Completing...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5 mr-2" />
                    Complete My Journey
                  </>
                )}
              </Button>
            </div>
          </>
        ) : (
          <div className="p-6 bg-muted/50 border rounded-lg text-center">
            <Trophy className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
            <p className="text-muted-foreground">
              Complete the video above to unlock the final step
            </p>
          </div>
        )}
      </div>
    );
  }

  // Normal acknowledgment step
  return (
    <div className="space-y-6">
      {/* Video Section */}
      {videoSection}

      {/* Show content only when video is complete */}
      {videoCompleted ? (
        <>
          {/* Content */}
          <div className="prose prose-sm max-w-none">
            <p className="text-muted-foreground whitespace-pre-line">{content}</p>
          </div>

          {/* Affiliate Link */}
          {showAffiliateLink && (
            <div className="p-4 bg-muted/50 rounded-lg">
              <p className="text-sm font-medium mb-2">Share with loved ones:</p>
              <p className="text-xs text-muted-foreground">
                Your unique referral link will be available in your settings after completing the journey.
              </p>
            </div>
          )}

          {/* Checkbox */}
          <div className="flex items-start gap-3 p-4 border rounded-lg">
            <Checkbox
              id={`ack-${stepId}`}
              checked={isChecked}
              onCheckedChange={(checked) => setIsChecked(checked === true)}
              className="mt-0.5"
            />
            <label
              htmlFor={`ack-${stepId}`}
              className="text-sm font-medium cursor-pointer leading-relaxed"
            >
              {checkboxText}
            </label>
          </div>

          {/* Continue Button */}
          <div className="flex justify-end">
            <Button
              onClick={handleContinue}
              disabled={!isChecked || isSubmitting}
              size="lg"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                "Acknowledge and Continue"
              )}
            </Button>
          </div>
        </>
      ) : (
        <div className="p-6 bg-muted/50 border rounded-lg text-center">
          <CheckCircle2 className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
          <p className="text-muted-foreground">
            Complete the video above to continue
          </p>
        </div>
      )}
    </div>
  );
}
